import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Gauge,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  Target,
  Brain,
  ThumbsUp,
  LayoutDashboard,
  Zap,
  Shield,
  Database,
  Cpu,
  RefreshCw,
  Play,
  ChevronRight,
  Gift,
  LineChart,
  DollarSign,
  Building2,
  Headphones,
  TrendingDown,
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { tawjihAITestimonials } from '../../shared/testimonialData';

const TawjihAI: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Database,
      title: 'Real-Time Customer Scoring Engine',
      description: 'Continuously evaluates customer profiles and behaviour to produce dynamic propensity scores for loans, deposits, cards, wealth products, and more'
    },
    {
      icon: Brain,
      title: 'Next-Best-Product Recommendations',
      description: 'AI models suggest personalised product bundles for each customer, factoring in affordability, risk appetite, regulatory rules, and relationship history.'
    },
    {
      icon: Zap,
      title: '	Omni-Channel Sales Orchestration',
      description: 'Pushes recommendations into branch CRM, call centres, RM workbenches, mobile banking apps, and email/SMS journeys so sales teams work from a single source of truth.'
    },
    {
      icon: LayoutDashboard,
      title: 'Automated Sales Workflows',
      description: 'Generates call lists, follow-up tasks, and campaign segments automatically, reducing manual list-building and repetitive admin.'
    },
    {
      icon: Target,
      title: 'Unified Relationship Dashboard',
      description: 'Gives teams a complete picture of each customer’s products, interactions, scores, and opportunities—ideal for RMs, product teams, and leadership.'
    },
    {
      icon: RefreshCw,
      title: '	Configurable Rules & Constraints',
      description: 'Business and risk teams can define guardrails, eligibility rules, and priority products without touching code.'
    },
    {
      icon: CheckCircle,
      title: '	Performance Analytics & A/B Testing',
      description: 'Tracks conversion, churn, and campaign impact, and supports test-and-learn experiments across segments and channels.'
    },
    {
      icon: Shield,
      title: '	Scalable, Bank-Grade Architecture',
      description: 'Built to scale with growing customer bases, new products, and evolving digital journeys, without repeated rework of models and pipelines.'
    }
  ];

  const recommendationFlow = [
    {
      icon: Database,
      title: 'Customer Data',
      subtitle: 'Data ingestion and profile building',
      description: 'Collection of customer profiles and transaction history from core banking systems, CRM, and digital channels.',
      color: 'from-cyan-400 to-blue-500',
      step: 1
    },
    {
      icon: Brain,
      title: 'AI Analysis',
      subtitle: 'Pattern recognition and segmentation',
      description: 'Machine learning algorithms identify patterns, segment customers, and calculate propensity scores for product recommendations.',
      color: 'from-blue-400 to-indigo-500',
      step: 2
    },
    {
      icon: ThumbsUp,
      title: 'Product Matching',
      subtitle: 'Next-best-product recommendation',
      description: 'Identifies suitable financial products based on customer needs, behavior, lifecycle stage, and predictive models.',
      color: 'from-indigo-400 to-purple-500',
      step: 3
    },
    {
      icon: LayoutDashboard,
      title: 'Dashboard View',
      subtitle: 'Centralized insights and orchestration',
      description: 'Centralized insights for bank staff with offer orchestration, campaign management, and performance analytics.',
      color: 'from-purple-400 to-pink-500',
      step: 4
    },
    {
      icon: TrendingUp,
      title: 'Conversion',
      subtitle: 'Enhanced engagement and revenue',
      description: 'Enhanced customer engagement and sales with feedback loops feeding back into the recommendation engine for continuous improvement.',
      color: 'from-pink-400 to-rose-500',
      step: 5
    }
  ];

  const solutions = [
    {
      icon: Cpu,
      title: 'Cross-Sell & Upsell in Retail Banking',
      description: 'Identify which customers are most likely to take personal loans, credit cards, or top-ups and route them to the right sales teams and channels.',
      features: ['Behavioral Analysis', 'Lifecycle Scoring', 'Risk Assessment', 'Churn Prediction', 'Cross-sell Opportunities']
    },
    {
      icon: Gift,
      title: '	SME & Corporate Relationship Banking',
      description: 'Help RMs spot timely opportunities for working capital lines, trade finance, treasury products, and digital collection solutions.',
      features: ['Campaign Automation', 'Channel Optimization', 'A/B Testing', 'Personalization Engine', 'Timing Optimization']
    },
    {
      icon: LineChart,
      title: '	Digital-Only Journeys',
      description: 'Embed Tawjih AI into mobile and internet banking so customers see relevant offers rather than generic banners.',
      features: ['Real-Time Metrics', 'ROI Tracking', 'Acceptance Rates', 'Revenue Attribution', 'Performance Benchmarks']
    },
    {
      icon: Shield,
      title: '	Branch Productivity & Call Centre Targeting',
      description: 'Equip front-line teams with daily lists of high-potential customers and context-rich talking points to make each interaction count.',
      features: ['Regulatory Compliance', 'Audit Trails', 'Risk Controls', 'Policy Enforcement', 'Data Privacy']
    },
    {
      icon: CheckCircle,
      title: '	Retention & Churn Management',
      description: 'Detect early churn signals and trigger retention offers, engagement campaigns, or RM outreach before value leaks.',
      features: ['Integration APIs', 'CRM Connectivity', 'Data Lake Support', 'Cloud-Native', 'Scalable Architecture']
    }
  ];

  const bankingUseCases = [
    {
      icon: Building2,
      title: 'Bank-Grade Data Protection',
      description: 'Customer data is encrypted in transit and at rest, with strict isolation between environments and tenants.',
    },
    {
      icon: Users,
      title: 'Role-Based Access Controls',
      description: 'Granular permissions ensure that only authorised teams can view or act on customer information and campaign data.',
    },
    {
      icon: DollarSign,
      title: 'Comprehensive Audit Trails',
      description: 'Every score change, rule adjustment, and campaign deployment is logged for internal and regulatory review.',
    },
    {
      icon: Headphones,
      title: '	Model Governance & Explainability',
      description: 'Transparent scoring logic and documentation support fair lending practices and internal model risk frameworks.',
    },
    {
      icon: Shield,
      title: 'Compliance-Ready Design',
      description: 'Architected to operate within banking regulatory environments, with deployment options that respect data residency and internal IT policies.',
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Higher Conversion & Product Penetration',
      description: 'Targeted, data-driven recommendations put the right offers in front of customers who genuinely need them.',
    },
    {
      icon: TrendingDown,
      title: 'Lower Cost per Acquisition',
      description: 'Automated segmentation and prioritisation mean sales teams spend more time on high-value interactions and less on cold outreach.',
    },
    {
      icon:TrendingUp ,
      title: 'Improved Customer Experience',
      description: 'Customers see relevant offers instead of random pitches, strengthening trust and long-term relationships',
    },
    {
      icon: Target,
      title: 'Faster Time-to-Market for New Products',
      description: 'New products can be plugged into the scoring and recommendation engine quickly, without rebuilding journeys from scratch.',
    },
    {
      icon: Brain,
      title: '	Unified View for Business & Risk',
      description: 'Shared dashboards create a single version of truth for business, analytics, and risk teams.',
    },
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-teal-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 neon-glow">
                  <Gauge className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-teal-400 uppercase tracking-wide">Real-Time AI Engine</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-teal-500 to-emerald-600 bg-clip-text text-transparent">Tawjih x AI</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Banking Recommender Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               Tawjih AI is a real-time intelligence layer for banks and financial institutions. It continuously scores customer data—transactions, demographics, behaviours, and life events—to surface the right product at the right moment, on the right channel. By automating large parts of the sales and targeting workflow, Tawjih AI feeds a unified dashboard for product, marketing, and branch teams. The result is higher conversion, sharper cross-sell, and lower cost per acquisition, without increasing pressure on frontline staff
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-teal-500 to-emerald-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-500">Real-Time</div>
                  <div className="text-sm text-muted-foreground">Scoring</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-400">Smart</div>
                  <div className="text-sm text-muted-foreground">Recommendations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Unified</div>
                  <div className="text-sm text-muted-foreground">Dashboard</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-teal-500 via-emerald-500 to-green-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/TawjihxAI.jpg"
                    alt="Tawjih AI Banking Recommender Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Intelligent banking solutions powered by real-time AI
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tawjih AI Recommendation Flow - Hardcoded from Image */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Tawjih AI <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Recommendation Flow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4 text-center">
              Recommendation Sequence
            </p>
            <p className="text-base text-muted-foreground max-w-4xl mx-auto text-center">
              The recommendation flow includes data ingestion, customer segmentation, propensity model, next-best-product, offer orchestration, and feedback loop. The dashboard tracks acceptance, revenue, and campaign ROI.
            </p>
          </motion.div>

          {/* Hardcoded Flowchart from Provided Image */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 relative">
              {recommendationFlow.map((step, index) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="glass-strong rounded-xl p-6 relative group hover:border-teal-500/50 transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 shadow-lg shadow-teal-500/30 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-semibold text-lg mb-2 text-white">{step.title}</h3>
                        <p className="text-xs text-muted-foreground mb-3">{step.subtitle}</p>
                        <p className="text-xs text-muted-foreground/80 text-justify">{step.description}</p>
                      </div>
                      {/* Arrow */}
                      {index < recommendationFlow.length - 1 && (
                        <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                          <ChevronRight className="w-6 h-6 text-teal-400" />
                        </div>
                      )}
                    </motion.div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Continuous Improvement Note */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 glass rounded-xl p-6 border border-teal-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Continuous Improvement</h3>
                  <p className="text-sm text-muted-foreground text-justify">
                    Tawjih AI continuously improves its recommendation capabilities, ensuring that users have access to the latest insights and tools for their business. Feedback loops capture customer acceptance, engagement, and conversion data to refine propensity models, optimize offer timing, and enhance overall campaign performance.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Enterprise-grade AI for intelligent banking operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-teal-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 hidden">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Banking Use Cases Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent"> Compliance</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Tailored solutions for diverse banking segments
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {bankingUseCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{useCase.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{useCase.description}</p>
                    </div>
                  </div>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Transform banking operations with intelligent AI
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-teal-500/50 hover:border-teal-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-teal-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <ProductTestimonials 
          testimonials={tawjihAITestimonials}
          title="Banking Transformation Success"
          subtitle="See how financial institutions boosted conversions with AI recommendations"
          gradientFrom="#0FFFA0"
          gradientVia="#00CFFF"
          gradientTo="#0FFFA0"
          iconColor="#0FFFA0"
          dotColor="bg-teal-500"
          speed={60}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-teal-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-transparent to-emerald-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Transform Your <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Banking Operations?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Schedule a demo to see how Tawjih AI can increase conversion rates, reduce operational costs, and deliver personalized financial product recommendations to your customers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-teal-500/30 hover:border-teal-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TawjihAI;
